import { useCallback, useEffect, useRef, useState } from "react";
import { MDBBtn, MDBRange } from "mdb-react-ui-kit";
import "./TimelineView.css";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import SectionEditor from "Projects/EXHI_exhibiciones_project/Components/SectionEditor/SectionEditor";

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 8;
const PX_PER_SEC_BASE = 5; // zoom×1: 5px/s
const MIN_BLOCK_PX   = 40;

function fmt(s) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// tiempoFin is calculated in frontend (not returned by API)
function secEnd(s) { return s.tiempoFin != null ? s.tiempoFin : s.tiempoInicio + s.duracion; }

export default function TimelineView({ exhibicion, canManage, onRefresh, externalSelectedId, onSelectSection, onTimeChange }) {
  const [zoom, setZoom]               = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying]         = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [editSeccion, setEditSeccion] = useState(null);
  const [dropping, setDropping]       = useState(false);

  // Section reorder DnD
  const [draggingSecId, setDraggingSecId] = useState(null);
  const [insertX, setInsertX]             = useState(null);

  // Media DnD
  const [mediaDropSecId, setMediaDropSecId]   = useState(null);
  const [trackMediaOver, setTrackMediaOver]   = useState(false);
  const [pinMediaOver, setPinMediaOver]       = useState(null);

  // Delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const effectiveSelectedId = externalSelectedId !== undefined ? externalSelectedId : selectedId;
  const intervalRef  = useRef(null);
  const startTimeRef = useRef(0);
  const trackRef     = useRef(null);
  const lastSecRef   = useRef(null);

  const handleEditorClose = useCallback(() => setEditSeccion(null), []);
  const handleEditorSaved = useCallback(() => {
    setEditSeccion(null);
    onRefresh?.();
  }, [onRefresh]);

  const secciones     = exhibicion?.secciones || [];
  const sortedSecs    = [...secciones].sort((a, b) => a.tiempoInicio - b.tiempoInicio);
  const totalDuration = sortedSecs.reduce((acc, s) => Math.max(acc, secEnd(s)), 0) || 60;
  const pxPerSec      = PX_PER_SEC_BASE * zoom;
  const totalWidth    = Math.max(totalDuration * pxPerSec + 80, 300);

  // Playback engine
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= totalDuration) { setPlaying(false); return startTimeRef.current; }
          return prev + 0.1;
        });
      }, 100);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, totalDuration]);

  // Auto-select section under playhead during playback
  useEffect(() => {
    if (!playing) return;
    const hit = sortedSecs.find((s) => currentTime >= s.tiempoInicio && currentTime < secEnd(s)) || null;
    const id  = hit?.idSeccion ?? null;
    if (id !== lastSecRef.current) {
      lastSecRef.current = id;
      if (hit) { setSelectedId(hit.idSeccion); onSelectSection?.(hit); }
    }
  }, [currentTime, playing]); // eslint-disable-line react-hooks/exhaustive-deps

  function togglePlay() {
    if (currentTime >= totalDuration) setCurrentTime(startTimeRef.current);
    setPlaying((p) => !p);
  }

  function setPlayhead(time) {
    startTimeRef.current = time;
    setCurrentTime(time);
    onTimeChange?.(time);
  }

  function handleTrackClick(e) {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const time = Math.max(0, (e.clientX - rect.left) / pxPerSec);
    setPlayhead(time);
    const hit = sortedSecs.find((s) => time >= s.tiempoInicio && time < secEnd(s));
    if (hit) { setSelectedId(hit.idSeccion); onSelectSection?.(hit); }
  }

  function handleSecClick(e, s) {
    e.stopPropagation();
    setSelectedId(s.idSeccion);
    setPlayhead(s.tiempoInicio);
    onSelectSection?.(s);
  }

  // Ruler ticks
  const rawTick  = 120 / pxPerSec;
  const tickStep = rawTick < 3 ? 2 : rawTick < 8 ? 5 : rawTick < 18 ? 10 : rawTick < 45 ? 30 : 60;
  const ticks    = [];
  for (let t = 0; t <= totalDuration + tickStep; t += tickStep) ticks.push(t);

  // Insertion pins: at time=0 and at secEnd of each section
  const insertionPins = [
    { time: 0, insertBefore: 0 },
    ...sortedSecs.map((s, i) => ({ time: secEnd(s), insertBefore: i + 1 })),
  ];

  // Section reorder helpers
  function getInsertBeforeFromX(x) {
    const time = x / pxPerSec;
    for (let i = 0; i < sortedSecs.length; i++) {
      const mid = (sortedSecs[i].tiempoInicio + secEnd(sortedSecs[i])) / 2;
      if (time < mid) return i;
    }
    return sortedSecs.length;
  }

  function getInsertLineX(insertBefore) {
    if (!sortedSecs.length || insertBefore === 0) return 0;
    const prev = sortedSecs[Math.min(insertBefore, sortedSecs.length) - 1];
    return secEnd(prev) * pxPerSec;
  }

  function handleSecDragStart(e, s) {
    e.dataTransfer.setData("application/exhi-section", JSON.stringify({ idSeccion: s.idSeccion }));
    e.dataTransfer.effectAllowed = "move";
    setDraggingSecId(s.idSeccion);
    setInsertX(null);
  }

  function handleSecDragEnd() {
    setDraggingSecId(null);
    setInsertX(null);
  }

  async function handleDeleteSection(idSeccion) {
    console.log(`[EXHI:Timeline] handleDeleteSection — idSeccion=${idSeccion}`);
    setConfirmDeleteId(null);
    setDropping(true);
    try {
      const res = await doFetchJson("EXHI", `secciones/${idSeccion}`, { method: "DELETE" });
      console.log("[EXHI:Timeline] handleDeleteSection — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Sección eliminada", type: "success" });
        onRefresh?.();
      } else {
        toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
      }
    } catch (err) {
      console.error("[EXHI:Timeline] handleDeleteSection — ERROR:", err);
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setDropping(false);
    }
  }

  async function reorderSection(draggedId, insertBefore) {
    const origIdx = sortedSecs.findIndex((s) => s.idSeccion === draggedId);
    if (origIdx === -1) { console.warn("[EXHI:Timeline] reorderSection — draggedId no encontrado:", draggedId); return; }
    const dragged = sortedSecs[origIdx];
    const without = sortedSecs.filter((s) => s.idSeccion !== draggedId);
    let at = insertBefore > origIdx ? insertBefore - 1 : insertBefore;
    at = Math.max(0, Math.min(at, without.length));
    const newOrder = [...without.slice(0, at), dragged, ...without.slice(at)];
    // No-op check: same order as before
    if (newOrder.map((s) => s.idSeccion).join() === sortedSecs.map((s) => s.idSeccion).join()) {
      console.log("[EXHI:Timeline] reorderSection — sin cambio de orden, abortando");
      return;
    }
    const sectionOrder = newOrder.map((s) => s.idSeccion).join(",");
    console.log(`[EXHI:Timeline] reorderSection — exhibicion=${exhibicion.idExhibicion} draggedId=${draggedId} insertBefore=${insertBefore} newOrder=${sectionOrder}`);
    setDropping(true);
    try {
      // Usar el endpoint atómico de reorden: el backend recalcula tiempoInicio sin validateNoOverlap
      const res = await doFetchJson("EXHI", `exhibiciones/${exhibicion.idExhibicion}/secciones/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ sectionOrder }).toString(),
      });
      console.log("[EXHI:Timeline] reorderSection — respuesta:", res?.STATE, res?.MESSAGE);
      if (res?.STATE !== "SUCCESS") throw new Error(res?.MESSAGE || "Error al reordenar secciones");
      onRefresh?.();
    } catch (err) {
      console.error("[EXHI:Timeline] reorderSection — ERROR:", err);
      toast.show({ message: err.message || "Error al reordenar secciones", type: "danger" });
    } finally {
      setDropping(false);
    }
  }

  // Media helpers
  async function addMediaToSection(idSeccion, media) {
    const isAudio = media.mediaType === "AUDIO";
    console.log(`[EXHI:Timeline] addMediaToSection — seccion=${idSeccion} uuid=${media.uuid} tipo=${media.mediaType} isAudio=${isAudio}`);
    const res = await doFetchJson("EXHI", `secciones/${idSeccion}/medios/${isAudio ? "audio" : "visual"}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ mfmUuid: media.uuid, mediaType: media.mediaType }).toString(),
    });
    console.log(`[EXHI:Timeline] addMediaToSection — respuesta:`, res?.STATE, res?.MESSAGE);
    if (res?.STATE !== "SUCCESS") throw new Error(res?.MESSAGE || "Error al añadir medio");
  }

  async function createSectionAtTime(tiempoInicio, media) {
    const duracion = media.duracion ? Math.ceil(media.duracion) : 60;
    console.log(`[EXHI:Timeline] createSectionAtTime — exhibicion=${exhibicion.idExhibicion} tiempoInicio=${tiempoInicio} duracion=${duracion} media="${media.titulo}" uuid=${media.uuid}`);
    const res = await doFetchJson("EXHI", `exhibiciones/${exhibicion.idExhibicion}/secciones`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ titulo: media.titulo || "Nueva sección", tiempoInicio: String(tiempoInicio), duracion: String(duracion) }).toString(),
    });
    console.log(`[EXHI:Timeline] createSectionAtTime — respuesta crear sección:`, res?.STATE, res?.MESSAGE, "id:", res?.DATA?.idSeccion);
    if (res?.STATE !== "SUCCESS") throw new Error(res?.MESSAGE || "Error al crear sección");
    await addMediaToSection(res.DATA.idSeccion, media);
    toast.show({ message: `Sección "${media.titulo || "Nueva sección"}" creada`, type: "success" });
    onRefresh?.();
  }

  // Section block: only handles media drops; section drops bubble up to track
  function handleSecDragOver(e) {
    if (!e.dataTransfer.types.includes("application/exhi-media")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }

  function handleSecDragEnter(e, id) {
    if (e.dataTransfer.types.includes("application/exhi-media")) setMediaDropSecId(id);
  }

  function handleSecDragLeave(e, id) {
    if (!e.currentTarget.contains(e.relatedTarget))
      setMediaDropSecId((prev) => (prev === id ? null : prev));
  }

  async function handleSecDrop(e, s) {
    setMediaDropSecId(null);
    if (!e.dataTransfer.types.includes("application/exhi-media")) return; // section drop bubbles to track
    e.preventDefault();
    e.stopPropagation();
    const raw = e.dataTransfer.getData("application/exhi-media");
    let media;
    try { media = JSON.parse(raw); } catch (err) { console.error("[EXHI:Timeline] handleSecDrop — parse error:", err); return; }
    console.log(`[EXHI:Timeline] handleSecDrop — media sobre sección idSeccion=${s.idSeccion} "${s.titulo}"`, media);
    setDropping(true);
    try {
      await addMediaToSection(s.idSeccion, media);
      toast.show({ message: `Medio añadido a "${s.titulo}"`, type: "success" });
      onRefresh?.();
    } catch (err) {
      console.error("[EXHI:Timeline] handleSecDrop — ERROR:", err);
      toast.show({ message: err.message || "Error de conexión", type: "danger" });
    } finally {
      setDropping(false);
    }
  }

  // Track: handles both media drops (create section) and section drops (reorder)
  // Section blocks don't preventDefault for section drags, so they bubble here.
  function handleTrackDragOver(e) {
    const hasMedia   = e.dataTransfer.types.includes("application/exhi-media");
    const hasSection = e.dataTransfer.types.includes("application/exhi-section");
    if (!hasMedia && !hasSection) return;
    e.preventDefault();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    if (hasSection) {
      e.dataTransfer.dropEffect = "move";
      const ib = getInsertBeforeFromX(x);
      setInsertX(getInsertLineX(ib));
    } else {
      e.dataTransfer.dropEffect = "copy";
      setTrackMediaOver(true);
    }
  }

  function handleTrackDragLeave(e) {
    if (!trackRef.current?.contains(e.relatedTarget)) {
      setTrackMediaOver(false);
      setInsertX(null);
    }
  }

  async function handleTrackDrop(e) {
    e.preventDefault();
    setTrackMediaOver(false);
    setInsertX(null);

    if (e.dataTransfer.types.includes("application/exhi-section")) {
      const raw = e.dataTransfer.getData("application/exhi-section");
      let draggedId;
      try { draggedId = JSON.parse(raw).idSeccion; } catch (err) { console.error("[EXHI:Timeline] handleTrackDrop — parse section error:", err); return; }
      const rect = trackRef.current?.getBoundingClientRect();
      const x = rect ? e.clientX - rect.left : 0;
      console.log(`[EXHI:Timeline] handleTrackDrop — sección reorden draggedId=${draggedId} x=${Math.round(x)}px insertBefore=${getInsertBeforeFromX(x)}`);
      await reorderSection(draggedId, getInsertBeforeFromX(x));
      return;
    }

    if (!e.dataTransfer.types.includes("application/exhi-media")) return;
    const raw = e.dataTransfer.getData("application/exhi-media");
    let media;
    try { media = JSON.parse(raw); } catch (err) { console.error("[EXHI:Timeline] handleTrackDrop — parse media error:", err); return; }
    const rect     = trackRef.current?.getBoundingClientRect();
    const dropTime = rect ? Math.max(0, Math.round((e.clientX - rect.left) / pxPerSec)) : 0;
    console.log(`[EXHI:Timeline] handleTrackDrop — media en track t=${dropTime}s`, media);
    setDropping(true);
    try {
      await createSectionAtTime(dropTime, media);
    } catch (err) {
      console.error("[EXHI:Timeline] handleTrackDrop — ERROR:", err);
      toast.show({ message: err.message || "Error de conexión", type: "danger" });
    } finally {
      setDropping(false);
    }
  }

  // Pin: accepts media drops to create a section at the pin's time boundary
  function handlePinDragOver(e, pinIdx) {
    if (!e.dataTransfer.types.includes("application/exhi-media")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setPinMediaOver(pinIdx);
  }

  function handlePinDragLeave(e, pinIdx) {
    if (!e.currentTarget.contains(e.relatedTarget))
      setPinMediaOver((prev) => (prev === pinIdx ? null : prev));
  }

  async function handlePinDrop(e, pin, pinIdx) {
    e.preventDefault();
    e.stopPropagation();
    setPinMediaOver(null);
    if (!e.dataTransfer.types.includes("application/exhi-media")) return;
    const raw = e.dataTransfer.getData("application/exhi-media");
    let media;
    try { media = JSON.parse(raw); } catch (err) { console.error("[EXHI:Timeline] handlePinDrop — parse error:", err); return; }
    console.log(`[EXHI:Timeline] handlePinDrop — media en pin idx=${pinIdx} t=${pin.time}s`, media);
    setDropping(true);
    try {
      await createSectionAtTime(pin.time, media);
    } catch (err) {
      console.error("[EXHI:Timeline] handlePinDrop — ERROR:", err);
      toast.show({ message: err.message || "Error de conexión", type: "danger" });
    } finally {
      setDropping(false);
    }
  }

  // Render
  const playheadLeft = Math.min(currentTime * pxPerSec, totalWidth - 2);

  return (
    <div>
      {/* Controls */}
      <div className="exhi-timeline-controls">
        <MDBBtn size="sm" color={playing ? "danger" : "primary"} onClick={togglePlay}>
          {playing ? "⏸ Pausa" : "▶ Play"}
        </MDBBtn>
        <MDBBtn size="sm" color="secondary" onClick={() => { setPlaying(false); setCurrentTime(startTimeRef.current); }}>
          ⏹ Stop
        </MDBBtn>
        <span className="exhi-timeline-time-display">{fmt(currentTime)}</span>
        <span className="zoom-label">Zoom:</span>
        <MDBRange
          min={MIN_ZOOM * 10}
          max={MAX_ZOOM * 10}
          value={zoom * 10}
          onChange={(e) => setZoom(Number(e.target.value) / 10)}
          style={{ width: 120 }}
        />
        <span className="zoom-label">{zoom.toFixed(1)}×</span>
        {canManage && (
          <MDBBtn size="sm" color="light" onClick={() => setEditSeccion({})}>
            + Sección
          </MDBBtn>
        )}
        {dropping && <span className="exhi-timeline-dropping">Procesando…</span>}
      </div>

      {/* Scrollable timeline */}
      <div className="exhi-timeline-wrap">
        <div className="exhi-tl-canvas" style={{ width: totalWidth }}>

          {/* Playhead — spans ruler + track */}
          <div className="exhi-tl-playhead" style={{ left: playheadLeft }} />

          {/* Ruler */}
          <div className="exhi-tl-ruler">
            {ticks.map((t) => (
              <span key={t} className="exhi-tl-tick" style={{ left: t * pxPerSec }}>
                {fmt(t)}
              </span>
            ))}
            {/* "+" insertion pins — also accept media drops to create a section here */}
            {canManage && insertionPins.map((pin, i) => (
              <div
                key={`pin-${i}`}
                className={`exhi-tl-pin${pinMediaOver === i ? " media-over" : ""}`}
                style={{ left: pin.time * pxPerSec }}
                onDragOver={(e) => handlePinDragOver(e, i)}
                onDragLeave={(e) => handlePinDragLeave(e, i)}
                onDrop={(e) => handlePinDrop(e, pin, i)}
              >
                <button
                  type="button"
                  className="exhi-tl-pin-btn"
                  onClick={() => setEditSeccion({})}
                  title="Nueva sección aquí"
                >+</button>
              </div>
            ))}
          </div>

          {/* Track */}
          <div
            ref={trackRef}
            className={`exhi-tl-track${trackMediaOver ? " media-over" : ""}${draggingSecId ? " sec-drag-active" : ""}`}
            onClick={handleTrackClick}
            onDragOver={handleTrackDragOver}
            onDragLeave={handleTrackDragLeave}
            onDrop={handleTrackDrop}
          >
            {/* Section blocks */}
            {sortedSecs.map((s) => {
              const left        = s.tiempoInicio * pxPerSec;
              const width       = Math.max(s.duracion * pxPerSec, MIN_BLOCK_PX);
              const isSelected  = effectiveSelectedId === s.idSeccion;
              const isDragging  = draggingSecId === s.idSeccion;
              const isMediaDrop = mediaDropSecId === s.idSeccion;
              const phPct       = isSelected && playing
                ? Math.max(0, Math.min(((currentTime - s.tiempoInicio) / (s.duracion || 1)) * 100, 100))
                : null;
              return (
                <div
                  key={s.idSeccion}
                  className={["exhi-tl-sec", isSelected ? "selected" : "", isDragging ? "dragging" : "", isMediaDrop ? "media-drop" : ""].filter(Boolean).join(" ")}
                  style={{ left, width }}
                  draggable={!!canManage}
                  onDragStart={(e) => handleSecDragStart(e, s)}
                  onDragEnd={handleSecDragEnd}
                  onDragOver={handleSecDragOver}
                  onDragEnter={(e) => handleSecDragEnter(e, s.idSeccion)}
                  onDragLeave={(e) => handleSecDragLeave(e, s.idSeccion)}
                  onDrop={(e) => handleSecDrop(e, s)}
                  onClick={(e) => handleSecClick(e, s)}
                  onDoubleClick={(e) => { e.stopPropagation(); canManage && setEditSeccion(s); }}
                  title={`${s.titulo} — ${fmt(s.tiempoInicio)}–${fmt(secEnd(s))} (${s.duracion}s)`}
                >
                  {s.medioVisual?.thumbUrl && (
                    <img src={s.medioVisual.thumbUrl} alt="" className="exhi-tl-sec-thumb" />
                  )}
                  <div className="exhi-tl-sec-info">
                    <span className="exhi-tl-sec-title">{s.titulo}</span>
                    <span className="exhi-tl-sec-dur">{fmt(s.tiempoInicio)} · {s.duracion}s</span>
                  </div>
                  {phPct !== null && (
                    <div className="exhi-tl-sec-ph" style={{ left: `${phPct}%` }} />
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="exhi-tl-sec-edit-btn"
                      onClick={(e) => { e.stopPropagation(); setEditSeccion(s); }}
                      title="Editar"
                    >✎</button>
                  )}
                  {canManage && confirmDeleteId === s.idSeccion ? (
                    <div className="exhi-tl-sec-confirm-del" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="exhi-tl-sec-del-yes"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSection(s.idSeccion); }}
                      >✓</button>
                      <button
                        type="button"
                        className="exhi-tl-sec-del-no"
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                      >✕</button>
                    </div>
                  ) : canManage ? (
                    <button
                      type="button"
                      className="exhi-tl-sec-del-btn"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(s.idSeccion); }}
                      title="Eliminar sección"
                    >🗑</button>
                  ) : null}
                </div>
              );
            })}

            {/* Yellow snap-line during section drag */}
            {insertX !== null && (
              <div className="exhi-tl-insert-line" style={{ left: insertX }} />
            )}
          </div>

        </div>
      </div>

      {editSeccion !== null && (
        <SectionEditor
          show={editSeccion !== null}
          setShow={handleEditorClose}
          idExhibicion={exhibicion.idExhibicion}
          seccion={editSeccion?.idSeccion ? editSeccion : null}
          onSaved={handleEditorSaved}
          onDeleted={handleEditorSaved}
        />
      )}
    </div>
  );
}
